import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, Filter, Loader2, Package2, Sparkles, Download, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PublicNavbar from '@/components/PublicNavbar';
import { LanguageFlag } from '@/components/LanguageFlag';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Designs() {
  const { t, i18n } = useTranslation();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [difficulty, setDifficulty] = useState(searchParams.get('difficulty') || 'all');
  const [showFreeOnly, setShowFreeOnly] = useState(searchParams.get('free') === 'true');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const observer = useRef();
  const navigate = useNavigate();

  // Category and difficulty translations
  const getCategoryLabel = (categoryKey) => {
    return t(`designs.category.${categoryKey}`, categoryKey);
  };

  const getDifficultyLabel = (difficultyKey) => {
    return t(`designs.difficulty.${difficultyKey}`, difficultyKey);
  };

  const CATEGORIES = [
    { value: 'all', labelKey: 'designs.allCategories' },
    { value: 'amigurumi', labelKey: 'designs.category.amigurumi' },
    { value: 'clothing', labelKey: 'designs.category.clothing' },
    { value: 'accessories', labelKey: 'designs.category.accessories' },
    { value: 'home', labelKey: 'designs.category.home' },
    { value: 'toys', labelKey: 'designs.category.toys' },
    { value: 'bags', labelKey: 'designs.category.bags' },
    { value: 'blankets', labelKey: 'designs.category.blankets' },
  ];

  const DIFFICULTIES = [
    { value: 'all', labelKey: 'designs.allLevels' },
    { value: 'beginner', labelKey: 'designs.difficulty.beginner' },
    { value: 'intermediate', labelKey: 'designs.difficulty.intermediate' },
    { value: 'advanced', labelKey: 'designs.difficulty.advanced' },
  ];

  const lastDesignRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setCurrentPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  useEffect(() => {
    setDesigns([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [searchTerm, category, difficulty, showFreeOnly, i18n.language]);

  // Normalize language code for API (handle variants like 'en-US@posix')
  const getApiLanguage = useCallback(() => {
    const lang = i18n.resolvedLanguage || i18n.language || 'en';
    return lang.split('-')[0].split('@')[0];  // 'en-US@posix' -> 'en'
  }, [i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    loadDesigns();
  }, [currentPage, searchTerm, category, difficulty, showFreeOnly, getApiLanguage]);

  const loadDesigns = async () => {
    if (currentPage === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', '12');
      // Only filter by language if we have a valid language
      const apiLang = getApiLanguage();
      if (apiLang && (apiLang === 'en' || apiLang === 'fi')) {
        params.append('language', apiLang);
      }
      if (searchTerm) params.append('search', searchTerm);
      if (category && category !== 'all') params.append('category', category);
      if (difficulty && difficulty !== 'all') params.append('difficulty', difficulty);
      if (showFreeOnly) params.append('is_free', 'true');

      const response = await axios.get(`${API}/designs?${params.toString()}`);
      const newItems = response.data.items || [];

      if (currentPage === 1) {
        setDesigns(newItems);
      } else {
        setDesigns(prev => [...prev, ...newItems]);
      }

      setTotalItems(response.data.total || 0);
      setHasMore(currentPage < (response.data.total_pages || 1));
    } catch (error) {
      logger.error('Failed to load designs:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (category !== 'all') params.set('category', category);
    if (difficulty !== 'all') params.set('difficulty', difficulty);
    if (showFreeOnly) params.set('free', 'true');
    setSearchParams(params);
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-background">
        <PublicNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">{t('designs.loadingDesigns')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicNavbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-4">
            {t('designs.title').split(' ')[0]} <span className="text-primary">{t('designs.title').split(' ').slice(1).join(' ')}</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('designs.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="rounded-3xl bg-white border border-stone-100 p-6 mb-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('designs.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-12 rounded-xl"
                />
              </div>
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder={t('designs.allCategories')} />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{t(cat.labelKey)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder={t('designs.allLevels')} />
              </SelectTrigger>
              <SelectContent>
                {DIFFICULTIES.map(diff => (
                  <SelectItem key={diff.value} value={diff.value}>{t(diff.labelKey)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} className="rounded-full bg-primary text-primary-foreground">
              <Filter className="h-4 w-4 mr-2" />
              {t('designs.applyFilters')}
            </Button>
          </div>
          <div className="mt-4 flex items-center">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showFreeOnly}
                onChange={(e) => setShowFreeOnly(e.target.checked)}
                className="rounded border-stone-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-muted-foreground">{t('designs.showFreeOnly')}</span>
            </label>
            <span className="ml-auto text-sm text-muted-foreground">
              {totalItems} {totalItems !== 1 ? t('designs.designsFound') : t('designs.designFound')}
            </span>
          </div>
        </div>

        {/* Designs Grid */}
        {designs.length === 0 ? (
          <div className="text-center py-20 rounded-3xl bg-white border border-stone-100">
            <Sparkles className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-semibold mb-2">{t('designs.noDesigns')}</h3>
            <p className="text-muted-foreground mb-6">
              {t('designs.noDesignsMessage')}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {designs.map((design, index) => (
                <div
                  key={design.design_id}
                  ref={index === designs.length - 1 ? lastDesignRef : null}
                  onClick={() => navigate(`/designs/${design.slug || design.design_id}`)}
                  className="group rounded-3xl bg-white border border-stone-100 overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer hover:-translate-y-1"
                >
                  {/* Image */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-rose-100 to-orange-100 relative overflow-hidden">
                    {design.images && design.images.length > 0 ? (
                      <img
                        src={design.images[0].url}
                        alt={design.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Sparkles className="h-16 w-16 text-primary/50" />
                      </div>
                    )}
                    {design.youtube_video_id && (
                      <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center">
                        <Play className="h-3 w-3 mr-1" />
                        {t('designs.video')}
                      </div>
                    )}
                    {/* Language flag */}
                    {design.language && (
                      <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full shadow-sm">
                        <LanguageFlag language={design.language} size="sm" />
                      </div>
                    )}
                    {design.is_free ? (
                      <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {t('designs.free')}
                      </div>
                    ) : (
                      <div className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-bold">
                        €{design.price}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">
                      {design.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {design.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(design.difficulty)}`}>
                        {getDifficultyLabel(design.difficulty)}
                      </span>
                      <div className="flex items-center space-x-2 text-muted-foreground text-sm">
                        {design.pdf_url && <Download className="h-4 w-4" />}
                        <span className="capitalize">{getCategoryLabel(design.category)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {loadingMore && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {!hasMore && designs.length > 0 && (
              <p className="text-center text-muted-foreground py-8">
                {t('designs.seenAll')} {totalItems} {totalItems !== 1 ? t('designs.designsFound').replace(' found', '') : t('designs.designFound').replace(' found', '')}
              </p>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Even Rounds. {t('common.madeWithLove')}
          </p>
        </div>
      </footer>
    </div>
  );
}
